import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useIsApiReady } from "../hooks/use-api-ready";
import { useGroupUuid } from "../hooks/use-group-uuid";
import { useKitUuid } from "../hooks/use-kit-uuid";
import {
  expertFolderControllerAssociate,
  expertFolderControllerDissociate,
  folderControllerGetFolders,
} from "../lib/gen-api";
import { queryKeys } from "../lib/query-keys";

export const useFoldersData = ({
  expertUuid,
}: {
  expertUuid: string | null;
}) => {
  const queryClient = useQueryClient();
  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();

  const { data: folders = [], isLoading } = useQuery({
    enabled: useIsApiReady() && !!expertUuid,
    initialData: [],
    queryKey: queryKeys.folders({ expertUuid: expertUuid! }),
    queryFn: async () => {
      const response = await folderControllerGetFolders({
        query: { filter: { expertUuid: expertUuid! } },
      });
      return response.data?.data ?? [];
    },
  });

  const invalidate = () => {
    if (expertUuid) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.folders({ expertUuid }),
      });
    }
    if (groupUuid && kitUuid) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitFolders(groupUuid, kitUuid),
      });
    }
  };

  const { mutateAsync: associateFolder, isPending: isAssociating } =
    useMutation({
      mutationFn: async ({ folderUuid }: { folderUuid: string }) => {
        if (!expertUuid) {
          throw new Error("Expert UUID is required");
        }

        return expertFolderControllerAssociate({
          body: { expertUuid, folderUuid },
        });
      },
      onSuccess: () => {
        toast.success("Folder set as Expert Knowledge");
      },
      onError: (error) => {
        console.error("Failed to associate folder:", error);
        toast.error("Failed to set as Expert Knowledge");
      },
      onSettled: invalidate,
    });

  const { mutateAsync: dissociateFolder, isPending: isDissociating } =
    useMutation({
      mutationFn: async ({ folderUuid }: { folderUuid: string }) => {
        if (!expertUuid) {
          throw new Error("Expert UUID is required");
        }

        return expertFolderControllerDissociate({
          body: { expertUuid, folderUuid },
        });
      },
      onSuccess: () => {
        toast.success("Folder removed from Expert Knowledge");
      },
      onError: (error) => {
        console.error("Failed to dissociate folder:", error);
        toast.error("Failed to remove from Expert Knowledge");
      },
      onSettled: invalidate,
    });

  return {
    folders,
    isLoading,
    associateFolder,
    dissociateFolder,
    isAssociating,
    isDissociating,
  };
};
